import React, { useEffect, useRef } from 'react';
import type { Path } from 'react-router';
import {
    Pagination as RoccoPagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@rocco/components/pagination';
import { PaginationEllipsis } from '@rocco/components/pagination/pagination-ellipsis';
import { getVisiblePages } from '@rocco/ui/pagination/utils/get-visible-pages';

interface TilePaginationProps {
    className?: string;
    currentPath: Path;
    current: number;
    total: number;
    visibleCount?: number;
    urlBuilder: (currentPath: Path, page: number) => string;
    scrollToRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * The standard pagination component. Automatically generates the pagination
 * links based on the current page, total pages, and visible count.
 */
export const Pagination = ({
    className,
    currentPath,
    current,
    total,
    visibleCount = 4,
    urlBuilder,
    scrollToRef,
}: TilePaginationProps) => {
    const visiblePages = getVisiblePages(current, total, visibleCount);
    const lastPageIndex = visiblePages.length > 0 ? visiblePages.length - 1 : 0;
    const lastPageToShow =
        (typeof visiblePages?.[lastPageIndex] === 'number'
            ? visiblePages[lastPageIndex]
            : total) ?? 0;

    const changingPage = useRef(false);

    const hasCustomScrollPosition = !!scrollToRef?.current;

    useEffect(() => {
        if (changingPage.current && scrollToRef?.current) {
            scrollToRef.current.scrollIntoView();
            changingPage.current = false;
        }
    }, [current, scrollToRef]);

    return (
        <RoccoPagination className={className}>
            <PaginationContent
                onClick={() => {
                    if (hasCustomScrollPosition) {
                        changingPage.current = true;
                    }
                }}
            >
                <PaginationItem>
                    <PaginationPrevious
                        to={urlBuilder(currentPath, current - 1)}
                        preventScrollReset={hasCustomScrollPosition}
                        isDisabled={current === 1}
                    />
                </PaginationItem>

                {visiblePages[0] && visiblePages[0] > 1 && (
                    <>
                        <PaginationItem>
                            <PaginationLink
                                to={urlBuilder(currentPath, 1)}
                                preventScrollReset={hasCustomScrollPosition}
                            >
                                1
                            </PaginationLink>
                        </PaginationItem>

                        {visiblePages[0] && visiblePages[0] > 2 && (
                            <PaginationItem>
                                <PaginationEllipsis />
                            </PaginationItem>
                        )}
                    </>
                )}

                {visiblePages.map((page) => (
                    <PaginationItem key={page}>
                        <PaginationLink
                            to={urlBuilder(currentPath, page)}
                            isActive={page === current}
                            preventScrollReset={hasCustomScrollPosition}
                        >
                            {page}
                        </PaginationLink>
                    </PaginationItem>
                ))}

                {lastPageToShow < total && (
                    <>
                        {lastPageToShow < total - 1 && (
                            <PaginationItem>
                                <PaginationEllipsis />
                            </PaginationItem>
                        )}

                        <PaginationItem>
                            <PaginationLink
                                to={urlBuilder(currentPath, total)}
                                preventScrollReset={hasCustomScrollPosition}
                            >
                                {total}
                            </PaginationLink>
                        </PaginationItem>
                    </>
                )}

                {current <= total && (
                    <PaginationItem>
                        <PaginationNext
                            to={urlBuilder(currentPath, current + 1)}
                            preventScrollReset={hasCustomScrollPosition}
                            isDisabled={current === total}
                        />
                    </PaginationItem>
                )}
            </PaginationContent>
        </RoccoPagination>
    );
};
